/**
 * An agent's `start_new_chat`: the user asked Denker, in any words, for a
 * new chat. The app does what its own New chat does: in Live Talk what the
 * new-chat chord does (listening goes on, the next spoken turn opens the new
 * chat), otherwise what `/new` does. A task given with the request opens the
 * new chat as the user's message, like `/new <task>`, and in Live Talk its
 * reply is spoken. Only the chat the user is addressing may ask: the bar's
 * target, or with no target Denker's current chat in Home (the backend's
 * `home_chat`, the one targetless input reaches). A helper, task or wake run
 * the user is not talking to never moves their chat or speaks as them. The
 * project being viewed plays no part.
 */
import type { WsMessage } from "@/api/websocket";
import { dispatchUserMessage } from "@/hooks/use-dispatch-user-message";
import { requestStandaloneNewChat } from "@/lib/new-chat-command";
import { isRemoteDeviceAction } from "@/lib/remote-device-frame";
import { useInputBarTarget } from "@/stores/input-bar-target-store";
import { isContinuousVoiceActive } from "./continuous-voice-store";
import { registerLiveTalkPromptRequest } from "./live-talk-speech-ownership";
import { startLiveTalkNewChat } from "./voice-new-chat-routing";

export const NEW_CHAT_REQUEST = "desktop.new_chat_request";

/** The turn that last started one: a repeated call in a turn starts no second chat. */
let handledExecutionId: string | null = null;

const text = (value: unknown): string =>
  typeof value === "string" ? value.trim() : "";

/** Called for every owner socket message; true when it was an agent's new-chat request. */
export function applyAgentNewChatRequest(msg: WsMessage): boolean {
  if (msg.type !== NEW_CHAT_REQUEST) return false;
  if (isRemoteDeviceAction(msg)) return true;
  const conversationId = text(msg.data.conversation_id);
  const firstMessage = text(msg.data.first_message);
  const bar = useInputBarTarget.getState();
  const barConversationId = bar.target?.conversationId;
  // With New armed, targetless input reaches no existing chat.
  const addressed = barConversationId
    ? barConversationId === conversationId
    : !bar.startNewConversation && msg.data.home_chat === true;
  if (!conversationId || !addressed) return true;
  const executionId = text(msg.data.execution_id);
  if (executionId && executionId === handledExecutionId) return true;
  handledExecutionId = executionId || null;
  const liveTalk = isContinuousVoiceActive();
  if (!firstMessage) {
    if (liveTalk) startLiveTalkNewChat();
    else requestStandaloneNewChat();
    return true;
  }
  requestStandaloneNewChat();
  const sent = dispatchUserMessage({
    text: firstMessage,
    literalText: true,
    composerId: "agent-new-chat",
    ...(liveTalk ? { promptSource: "continuous" as const } : {}),
  });
  // Denker's "New chat started." still plays; this reply follows it.
  if (liveTalk && sent.sent && sent.clientRequestId)
    registerLiveTalkPromptRequest(sent.clientRequestId, true);
  return true;
}
